import { useEffect } from 'react'
import { useRouter } from 'next/router'
import { useQuery, useQueryClient } from 'react-query'
import { useClient } from '@hooks/useClient'
import { useAlert } from '@hooks/useAlert'
import { useAuthentication } from '@hooks/useAuthentication'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { useUserAction } from '@hooks/user/useUserAction'
import { FollowSuccessStatusEnum } from '@interfaces/FollowSuccessStatusEnum'
import { UserProfileType } from '@models/user/UserProfileType'
import { toast } from 'react-toastify'
import { ErrorIcon } from '@components/icons/ErrorIcon'
import { ProfilePageProps, WithProfileLayoutProps } from './interface'

export function withProfilePage(Component: React.FC<ProfilePageProps>) {
  function WithProfilePage({ slug }: WithProfileLayoutProps) {
    const client = useClient()
    const queryClient = useQueryClient()
    const alert = useAlert()
    const router = useRouter()
    const userClient = useUserAction()
    const { user, isAuthenticated } = useAuthentication()
    const loginModal = useModal({ modal: 'login' })

    const {
      data: profile,
      isLoading,
      isError,
    } = useQuery(
      ['other-profile', slug],
      () => userClient.getProfileBySlug(slug),
      { enabled: !!slug }
    )
    useEffect(() => {
      if (isError) {
        alert.error('เกิดข้อผิดพลาดในการโหลดข้อมูลผู้ใช้')
        router.push('/')
      }
    }, [router, alert, isError])

    async function handleFollow() {
      if (isAuthenticated && profile) {
        try {
          const type = await userClient.toggleFollowUser(profile.id)
          queryClient.setQueryData<UserProfileType | undefined>(
            ['other-profile', slug],
            (oldData?: UserProfileType) => {
              if (oldData) {
                return {
                  ...oldData,
                  isFollowed: type === FollowSuccessStatusEnum.CREATE,
                }
              }

              return oldData
            }
          )
          if (type === FollowSuccessStatusEnum.CREATE) {
            alert.success('ติดตามผู้ใช้สำเร็จ')
          } else {
            alert.success('เลิกติดตามผู้ใช้สำเร็จ')
          }
        } catch (error) {
          alert.error('เกิดข้อผิดพลาดในการติดตามผู้ใช้')
        }
      } else {
        loginModal.show()
      }
    }

    function handleActiveTabChange(tabId: string) {
      router.replace(`/profile/${slug}?tab=${tabId}`, undefined, {
        scroll: false,
      })
    }

    if (!isLoading && profile?.isPermanentBan) {
      toast.error('ขออภัย บัญชีผู้ใช้นี้ถูกลบไปแล้ว', {
        toastId: 1,
        icon: <ErrorIcon />,
      })
      router.push('/')
      return null
    }

    if (!profile || isLoading) {
      return null
    }

    const componentProps = {
      user: profile,
      isProfileOwner: profile.id !== user.id,
      handleFollow,
      handleActiveTabChange,
      slug,
    }

    return <Component {...componentProps} />
  }

  return WithProfilePage
}
